import { useState } from 'react';
import { Check, Copy, KeyRound, TriangleAlert } from 'lucide-react';

interface Credenciales {
  username: string;
  passwordTemporal: string;
}

/**
 * Credenciales recién generadas.
 *
 * La contraseña temporal solo existe en esta respuesta: el servidor guarda su
 * hash y no puede volver a mostrarla. Por eso el aviso es explícito y hay un
 * botón para copiar las dos de una vez — dictar una contraseña de diez
 * caracteres por teléfono es la vía rápida a un comerciante que no puede
 * entrar.
 */
export function TarjetaCredenciales({ credenciales }: { credenciales: Credenciales }) {
  const [copiado, setCopiado] = useState<string | null>(null);

  const copiar = async (texto: string, cual: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(cual);
      setTimeout(() => setCopiado(null), 2000);
    } catch {
      // Sin permiso de portapapeles queda a la vista para copiarla a mano.
    }
  };

  return (
    <div className="rounded-kick border border-amber-200 bg-amber-50 p-4">
      <p className="flex items-start gap-2 text-sm font-semibold text-amber-900">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        Esta contraseña no se vuelve a mostrar
      </p>

      <dl className="mt-3 space-y-2">
        <Renglon
          etiqueta="Usuario"
          valor={credenciales.username}
          copiado={copiado === 'usuario'}
          onCopiar={() => void copiar(credenciales.username, 'usuario')}
        />
        <Renglon
          etiqueta="Contraseña temporal"
          valor={credenciales.passwordTemporal}
          copiado={copiado === 'clave'}
          onCopiar={() => void copiar(credenciales.passwordTemporal, 'clave')}
        />
      </dl>

      <button
        type="button"
        onClick={() =>
          void copiar(
            `Usuario: ${credenciales.username}\nContraseña temporal: ${credenciales.passwordTemporal}`,
            'ambas',
          )
        }
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-kick bg-amber-900 py-2 text-sm font-semibold text-white transition hover:bg-amber-800"
      >
        {copiado === 'ambas' ? <Check className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
        {copiado === 'ambas' ? 'Copiadas' : 'Copiar las dos'}
      </button>
    </div>
  );
}

function Renglon({
  etiqueta,
  valor,
  copiado,
  onCopiar,
}: {
  etiqueta: string;
  valor: string;
  copiado: boolean;
  onCopiar: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <dt className="w-40 shrink-0 text-sm text-amber-800">{etiqueta}</dt>
      <dd className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 font-mono text-sm text-kick-ink">
        {valor}
      </dd>
      <button
        type="button"
        onClick={onCopiar}
        aria-label={`Copiar ${etiqueta.toLowerCase()}`}
        className="rounded p-1.5 text-amber-800 transition hover:bg-amber-100"
      >
        {copiado ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}
